// frontend/src/components/Events/EditCoverModal.js
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { eventsAPI } from '../../services/eventsAPI';
import { styled } from '@mui/material/styles';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ============================================
// iOS 26 / Glass — visual language from CreateEditNewsModal
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(860px, calc(100vw - 32px))',
    maxWidth: 860,
    minWidth: 0,
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.74)},
        ${alpha(theme.palette.grey[100], 0.46)} 46%,
        ${alpha(theme.palette.primary.main, 0.09)}
      ),
      radial-gradient(circle at 8% 4%, ${alpha(theme.palette.info.light, 0.16)}, transparent 34%),
      radial-gradient(circle at 92% 90%, ${alpha(theme.palette.primary.main, 0.15)}, transparent 42%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.56),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.6, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.14)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2.8, 3),
  background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.16)}, ${alpha(theme.palette.primary.main, 0.025)})`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: '999px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.24),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.48),
    },
  },
}));

const GlassSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.42)},
      ${alpha(theme.palette.common.white, 0.16)}
    ),
    radial-gradient(
      circle at 100% 0%,
      ${alpha(theme.palette.primary.main, 0.055)},
      transparent 45%
    )
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.28),
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.045)}`,
}));

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      mb: 1,
      color: 'text.secondary',
      fontWeight: 600,
      fontSize: '0.72rem',
      letterSpacing: 0.45,
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

const DropzoneArea = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== 'isdragactive' && prop !== 'disabled',
})(({ theme, isdragactive, disabled }) => ({
  padding: theme.spacing(3.2),
  textAlign: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  width: '100%',
  borderRadius: theme.spacing(3.5),
  border: `1.5px dashed ${
    isdragactive === 'true'
      ? alpha(theme.palette.primary.main, 0.72)
      : alpha(theme.palette.common.white, 0.82)
  }`,
  background: `
    linear-gradient(
      145deg,
      ${alpha(theme.palette.common.white, 0.52)},
      ${alpha(theme.palette.common.white, 0.18)}
    ),
    radial-gradient(
      circle at 50% 0%,
      ${alpha(theme.palette.primary.main, 0.08)},
      transparent 48%
    )
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.36),
  backdropFilter: 'blur(18px) saturate(135%)',
  WebkitBackdropFilter: 'blur(18px) saturate(135%)',
  boxShadow: `0 12px 34px ${alpha(theme.palette.common.black, 0.06)}`,
  opacity: disabled ? 0.58 : 1,
  transition:
    'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',

  '&:hover': disabled
    ? {}
    : {
        borderColor: alpha(theme.palette.primary.main, 0.54),
        backgroundColor: alpha(theme.palette.common.white, 0.48),
        boxShadow: `0 16px 40px ${alpha(
          theme.palette.common.black,
          0.09
        )}`,
        transform: 'translateY(-1px)',
      },
}));

const MediaCard = styled(Paper)(({ theme, selected }) => ({
  borderRadius: theme.spacing(2.8),
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${
    selected
      ? alpha(theme.palette.primary.main, 0.56)
      : alpha(theme.palette.common.white, 0.78)
  }`,
  background: `
    linear-gradient(
      145deg,
      ${alpha(theme.palette.common.white, 0.58)},
      ${alpha(theme.palette.common.white, 0.22)}
    )
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.38),
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  boxShadow: selected
    ? `0 0 0 3px ${alpha(
        theme.palette.primary.main,
        0.10
      )},
       0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`
    : `0 10px 30px ${alpha(theme.palette.common.black, 0.08)}`,
  cursor: 'pointer',
  transition:
    'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',

  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: selected
      ? `0 0 0 3px ${alpha(
          theme.palette.primary.main,
          0.12
        )},
         0 16px 36px ${alpha(theme.palette.primary.main, 0.14)}`
      : `0 14px 34px ${alpha(theme.palette.common.black, 0.11)}`,

    '& .delete-overlay': {
      opacity: 1,
    },
  },
}));

const DeleteOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: `linear-gradient(
    145deg,
    ${alpha(theme.palette.common.black, 0.10)},
    ${alpha(theme.palette.common.black, 0.46)}
  )`,
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 180ms ease',
  zIndex: 2,
}));

const GlassIconButton = styled(IconButton)(({ theme }) => ({
  width: 42,
  height: 42,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.common.white, 0.48),
  border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
  boxShadow: `0 7px 20px ${alpha(theme.palette.common.black, 0.07)}`,

  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.72),
  },
}));

const GlassButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  minHeight: 44,
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  fontWeight: 650,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.common.white, 0.48),
  border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.055)}`,

  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.72),
    borderColor: alpha(theme.palette.common.white, 0.96),
    boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.08)}`,
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  minHeight: 44,
  paddingLeft: theme.spacing(2.8),
  paddingRight: theme.spacing(2.8),
  fontWeight: 700,
  color: theme.palette.primary.contrastText,

  background: `linear-gradient(
    135deg,
    ${alpha(theme.palette.primary.main, 0.96)},
    ${alpha(theme.palette.primary.dark, 0.96)}
  )`,

  boxShadow: `0 10px 28px ${alpha(
    theme.palette.primary.main,
    0.22
  )}`,

  transition:
    'transform 180ms ease, box-shadow 180ms ease, background 180ms ease',

  '&:hover': {
    background: `linear-gradient(
      135deg,
      ${theme.palette.primary.main},
      ${theme.palette.primary.dark}
    )`,
    boxShadow: `0 13px 34px ${alpha(
      theme.palette.primary.main,
      0.30
    )}`,
    transform: 'translateY(-1px)',
  },

  '&:disabled': {
    color: alpha(theme.palette.primary.contrastText, 0.78),
    background: alpha(theme.palette.primary.main, 0.48),
  },
}));

const InfoPill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.7),
  padding: theme.spacing(0.55, 1.0),
  borderRadius: 999,
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.common.white, 0.38),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  fontSize: '0.74rem',
  lineHeight: 1.2,
}));

// ============================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================

const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `https://fincollab.ru${path}`;
};

const checkImageOrientation = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const isLandscape = img.width > img.height;
        const isSquare = Math.abs(img.width - img.height) < 10;
        resolve(isLandscape || isSquare);
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
};

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EditCoverModal = ({
  open,
  onClose,
  eventId,
  currentImages = [],
  onSave,
}) => {
  const theme = useTheme();

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (files.length + acceptedFiles.length > MAX_IMAGES) {
        setError(`Максимум ${MAX_IMAGES} изображений`);
        return;
      }

      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map((f) => {
          if (f.file.size > MAX_FILE_SIZE) {
            return `${f.file.name}: файл слишком большой (макс. 5MB)`;
          }

          return `${f.file.name}: недопустимый формат (JPEG, PNG, WEBP, GIF)`;
        });

        setError(errors.join('. '));
        return;
      }

      setError('');

      for (const file of acceptedFiles) {
        const isValid = await checkImageOrientation(file);

        if (!isValid) {
          setError(
            `Изображение "${file.name}" должно быть горизонтальным или квадратным`
          );
          return;
        }
      }

      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,

    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
    },

    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_IMAGES - files.length,
  });

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveImage = async (imageUrl) => {
    if (!window.confirm('Удалить это изображение?')) return;

    try {
      const newImages = currentImages.filter(
        (url) => url !== imageUrl
      );

      await eventsAPI.updateCoverImages(eventId, newImages);

      onSave(newImages);

      if (selectedImage === imageUrl) {
        setSelectedImage(null);
      }
    } catch (err) {
      console.error('Ошибка удаления:', err);
      setError('Не удалось удалить изображение');
    }
  };

  const handleSetAsCurrent = (imageUrl) => {
    setSelectedImage(imageUrl);

    // TODO: отправить запрос на установку этого изображения как текущего
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const formData = new FormData();
        formData.append('image', file);

        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 0,
        }));

        const response = await eventsAPI.uploadCoverImage(
          eventId,
          formData,
          (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: progress,
            }));
          }
        );

        uploadedUrls.push(response.url);

        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }

      const allImages = [...currentImages, ...uploadedUrls];

      await eventsAPI.updateCoverImages(
        eventId,
        allImages
      );

      onSave(allImages);
      setFiles([]);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      setError('Не удалось загрузить изображения');
    } finally {
      setUploading(false);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      {/* ==========================================
          HEADER
      =========================================== */}

      <StyledDialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: alpha(
                theme.palette.primary.main,
                0.11
              ),
              border: `1px solid ${alpha(
                theme.palette.common.white,
                0.76
              )}`,
              boxShadow: `0 8px 22px ${alpha(
                theme.palette.primary.main,
                0.10
              )}`,
            }}
          >
            <ImageIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 650,
                fontSize: {
                  xs: '1.15rem',
                  sm: '1.3rem',
                },
                lineHeight: 1.25,
                color: 'text.primary',
              }}
            >
              Обложка мероприятия
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.35,
                color: 'text.secondary',
                fontWeight: 350,
                fontSize: '0.82rem',
              }}
            >
              Управляйте изображениями мероприятия
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            ml: 2,
            flexShrink: 0,
            width: 38,
            height: 38,
            color: 'text.secondary',
            backgroundColor: alpha(
              theme.palette.common.white,
              0.42
            ),
            border: `1px solid ${alpha(
              theme.palette.common.white,
              0.78
            )}`,
            boxShadow: `0 6px 18px ${alpha(
              theme.palette.common.black,
              0.05
            )}`,

            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(
                theme.palette.common.white,
                0.70
              ),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      {/* ==========================================
          CONTENT
      =========================================== */}

      <StyledDialogContent>
        {/* Ошибка */}

        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 2.2,
              borderRadius: 3,
              border: `1px solid ${alpha(
                theme.palette.error.main,
                0.18
              )}`,
              background: `
                linear-gradient(
                  145deg,
                  ${alpha(
                    theme.palette.error.light,
                    0.18
                  )},
                  ${alpha(
                    theme.palette.common.white,
                    0.34
                  )}
                )
              `,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: `0 10px 28px ${alpha(
                theme.palette.common.black,
                0.05
              )}`,
            }}
          >
            {error}
          </Alert>
        )}

        {/* ==========================================
            CURRENT IMAGES
        =========================================== */}

        {currentImages.length > 0 && (
          <GlassSection sx={{mt: 2, mb: 2.2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 1.8,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <SectionLabel>
                  Текущие изображения
                </SectionLabel>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.98rem',
                    lineHeight: 1.25,
                  }}
                >
                  Обложки мероприятия
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.35,
                    color: 'text.secondary',
                    fontWeight: 350,
                    fontSize: '0.78rem',
                  }}
                >
                  Нажмите на изображение, чтобы выделить его
                </Typography>
              </Box>

              <InfoPill>
                <ImageIcon sx={{ fontSize: 15 }} />
                {currentImages.length} / {MAX_IMAGES}
              </InfoPill>
            </Box>

            <Grid container spacing={1.6}>
              {currentImages.map((url, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <MediaCard
                    selected={selectedImage === url}
                    onClick={() =>
                      handleSetAsCurrent(url)
                    }
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: 132,
                        backgroundColor: alpha(
                          theme.palette.grey[200],
                          0.42
                        ),
                      }}
                    >
                      <Box
                        component="img"
                        src={getFullImageUrl(url)}
                        alt={`Обложка ${index + 1}`}
                        onError={(e) => {
                          e.currentTarget.style.display =
                            'none';
                        }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'block',
                          objectFit: 'cover',
                          transition:
                            'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',

                          '.MuiPaper-root:hover &': {
                            transform: 'scale(1.035)',
                          },
                        }}
                      />

                      {/* Номер изображения */}

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          px: 0.9,
                          py: 0.45,
                          borderRadius: 999,
                          color: 'white',
                          backgroundColor:
                            'rgba(18, 26, 42, 0.24)',
                          border:
                            '1px solid rgba(255,255,255,0.42)',
                          backdropFilter:
                            'blur(12px) saturate(145%)',
                          WebkitBackdropFilter:
                            'blur(12px) saturate(145%)',
                          fontSize: '0.68rem',
                          fontWeight: 650,
                          zIndex: 3,
                        }}
                      >
                        {index + 1}
                      </Box>

                      {/* Удаление */}

                      <DeleteOverlay className="delete-overlay">
                        <GlassIconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(url);
                          }}
                          sx={{
                            color: 'white',
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.82
                            ),
                            borderColor: alpha(
                              theme.palette.common.white,
                              0.46
                            ),

                            '&:hover': {
                              color: 'white',
                              backgroundColor:
                                theme.palette.error.main,
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </GlassIconButton>
                      </DeleteOverlay>

                      {/* Выбранное изображение */}

                      {selectedImage === url && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            width: 27,
                            height: 27,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '50%',
                            color: 'primary.main',
                            backgroundColor: alpha(
                              theme.palette.common.white,
                              0.78
                            ),
                            border: `1px solid ${alpha(
                              theme.palette.common.white,
                              0.92
                            )}`,
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter:
                              'blur(12px)',
                            boxShadow: `0 6px 18px ${alpha(
                              theme.palette.common.black,
                              0.10
                            )}`,
                            zIndex: 4,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ fontSize: 18 }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 1.15 }}>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        Обложка {index + 1}
                      </Typography>
                    </Box>
                  </MediaCard>
                </Grid>
              ))}
            </Grid>
          </GlassSection>
        )}

        {/* ==========================================
            UPLOAD AREA
        =========================================== */}

        <GlassSection sx={{ mb: 2.2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              mb: 1.8,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: 'primary.main',
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  0.10
                ),
                border: `1px solid ${alpha(
                  theme.palette.common.white,
                  0.68
                )}`,
              }}
            >
              <UploadIcon fontSize="small" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.98rem',
                }}
              >
                Добавить изображения
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 350,
                  fontSize: '0.78rem',
                }}
              >
                Горизонтальные или квадратные изображения
              </Typography>
            </Box>
          </Box>

          <DropzoneArea
            {...getRootProps()}
            isdragactive={
              isDragActive ? 'true' : 'false'
            }
            disabled={
              uploading ||
              files.length >= MAX_IMAGES
            }
          >
            <input {...getInputProps()} />

            <Box
              sx={{
                width: 58,
                height: 58,
                mx: 'auto',
                mb: 1.5,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: 'primary.main',
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  isDragActive ? 0.15 : 0.09
                ),
                border: `1px solid ${alpha(
                  theme.palette.common.white,
                  0.72
                )}`,
                boxShadow: `0 10px 26px ${alpha(
                  theme.palette.primary.main,
                  0.08
                )}`,
              }}
            >
              <UploadIcon sx={{ fontSize: 27 }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.92rem',
              }}
            >
              {isDragActive
                ? 'Отпустите файлы здесь'
                : 'Перетащите изображения сюда'}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                fontSize: '0.78rem',
              }}
            >
              или кликните для выбора файлов
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 0.8,
                flexWrap: 'wrap',
                mt: 1.6,
              }}
            >
              <InfoPill>JPEG</InfoPill>
              <InfoPill>PNG</InfoPill>
              <InfoPill>WEBP</InfoPill>
              <InfoPill>GIF</InfoPill>
              <InfoPill>до 5 MB</InfoPill>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.4,
                color: 'text.secondary',
              }}
            >
              Можно добавить ещё{' '}
              {Math.max(
                0,
                MAX_IMAGES -
                  currentImages.length -
                  files.length
              )}{' '}
              из {MAX_IMAGES}
            </Typography>
          </DropzoneArea>
        </GlassSection>

        {/* ==========================================
            NEW FILES
        =========================================== */}

        {files.length > 0 && (
          <GlassSection sx={{ mb: 2.2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.8,
              }}
            >
              <Box>
                <SectionLabel>
                  Новые изображения
                </SectionLabel>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.98rem',
                  }}
                >
                  Готовы к загрузке
                </Typography>
              </Box>

              <InfoPill>
                {files.length} файл
                {files.length === 1
                  ? ''
                  : files.length < 5
                  ? 'а'
                  : 'ов'}
              </InfoPill>
            </Box>

            <Grid container spacing={1.6}>
              {files.map((file, index) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  key={`${file.name}-${index}`}
                >
                  <MediaCard>
                    <Box
                      sx={{
                        position: 'relative',
                        height: 132,
                        backgroundColor: alpha(
                          theme.palette.grey[200],
                          0.42
                        ),
                      }}
                    >
                      <Box
                        component="img"
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'block',
                          objectFit: 'cover',
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          px: 0.9,
                          py: 0.45,
                          borderRadius: 999,
                          color: 'text.primary',
                          backgroundColor:
                            'rgba(255,255,255,0.62)',
                          border:
                            '1px solid rgba(255,255,255,0.78)',
                          backdropFilter:
                            'blur(12px) saturate(145%)',
                          WebkitBackdropFilter:
                            'blur(12px) saturate(145%)',
                          fontSize: '0.68rem',
                          fontWeight: 650,
                          zIndex: 3,
                        }}
                      >
                        Новое
                      </Box>

                      <DeleteOverlay className="delete-overlay">
                        <GlassIconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          sx={{
                            color: 'white',
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.82
                            ),
                            borderColor: alpha(
                              theme.palette.common.white,
                              0.46
                            ),

                            '&:hover': {
                              color: 'white',
                              backgroundColor:
                                theme.palette.error.main,
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </GlassIconButton>
                      </DeleteOverlay>
                    </Box>

                    <Box sx={{ p: 1.15 }}>
                      <Typography
                        variant="caption"
                        display="block"
                        noWrap
                        sx={{ fontWeight: 600 }}
                      >
                        {file.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {(
                          file.size /
                          (1024 * 1024)
                        ).toFixed(1)}{' '}
                        MB
                      </Typography>
                    </Box>
                  </MediaCard>
                </Grid>
              ))}
            </Grid>
          </GlassSection>
        )}

        {/* ==========================================
            UPLOAD PROGRESS
        =========================================== */}

        {Object.keys(uploadProgress).length > 0 && (
          <GlassSection sx={{ mb: 0.5 }}>
            <SectionLabel>Загрузка</SectionLabel>

            {Object.entries(uploadProgress).map(
              ([fileName, progress]) => (
                <Box
                  key={fileName}
                  sx={{
                    mb: 1.4,
                    '&:last-child': {
                      mb: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.7,
                    }}
                  >
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        maxWidth: '80%',
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {fileName}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700 }}
                    >
                      {progress}%
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 7,
                      borderRadius: 999,
                      backgroundColor: alpha(
                        theme.palette.common.white,
                        0.48
                      ),
                      border: `1px solid ${alpha(
                        theme.palette.common.white,
                        0.62
                      )}`,

                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                      },
                    }}
                  />
                </Box>
              )
            )}
          </GlassSection>
        )}
      </StyledDialogContent>

      {/* ==========================================
          ACTIONS
      =========================================== */}

      <DialogActions
        sx={{
          p: {
            xs: 2,
            sm: 2.4,
          },
          pt: 1.4,
          gap: 1,
          flexShrink: 0,

          background: `linear-gradient(
            135deg,
            ${alpha(theme.palette.common.white, 0.34)},
            ${alpha(theme.palette.common.white, 0.12)}
          )`,

          borderTop: `1px solid ${alpha(
            theme.palette.common.white,
            0.58
          )}`,
        }}
      >
        <GlassButton
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          Отмена
        </GlassButton>

        <PrimaryButton
          onClick={handleUpload}
          variant="contained"
          disabled={
            files.length === 0 || uploading
          }
          startIcon={
            uploading ? (
              <CircularProgress
                size={19}
                color="inherit"
              />
            ) : (
              <CheckCircleIcon />
            )
          }
        >
          {uploading
            ? 'Загрузка...'
            : 'Сохранить'}
        </PrimaryButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditCoverModal;